/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *|
 *| IBM Cognos Products: dashboard-core
 *|
 *| (C) Copyright IBM Corp. 2015, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/*
 * !!WARNING!! !!WARNING!! !!WARNING!!
 *
 * This file is used/inherited in Storytelling.
 * Please make sure changes in here do not break Storytelling or at least let us know.
 */

define([
	'underscore',
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../app/util/ErrorUtils',
	'../../../app/util/CapabilityUtils',
	'../../../dashboard/glass/util/SaveActionUtil'
], function (_, BaseClass, ErrorUtils, CapabilityUtils, SaveActionUtil) {

	const saveResponseFields = ['status', 'name', 'saveRequest', 'assetId', 'url', 'savedSpec', 'cmSpec'];
	const  ActionHandler = BaseClass.extend({

		getContentView: function (context) {
			return context.glassContext.appController.currentAppView.currentContentView;
		},

		getDashboardView: function (context) {
			return this.getContentView(context);
		},

		getStringResourcesService: function(context) {
			return this.getContentView(context).services.getSvcSync('.StringResources');
		},

		_getDashboardApi: function(context) {
			return this.getContentView(context).getDashboardApi();
		},

		_getContextContent: function (context) {
			return this.getContentView(context).getContent();
		},

		/**
		 * Handle the save button click. If the dashboard has an id, then we will save the dashboard. otherwise we will do a save as
		 */
		onSelectItem: function (context) {
			return CapabilityUtils.hasCapabilityToSaveDashboard(context, /*showToastMessage*/true)
				.then((hasCapability) => {
					if (hasCapability) {
						const dashboardView = this.getDashboardView(context);
						const id = dashboardView.getBoardId();
						const saveMode = context.target.itemId === 'com.ibm.bi.dashboard.save';
						if (id && saveMode && dashboardView.canAuthor()) {
							this.save(context, true, true/*bShowToast*/);
						} else {
							this.saveAs(context, id);
						}
					}
				});
		},

		isItemVisible: function (context) {
			// explore and dashboard shares the save button, but dashboard capability should not affect explore user to save exploration
			// TODO: clean this up once explore has their own button contribution
			const appViewType = this.getContentView(context).getDashboardApi().getType().toUpperCase();
			const saveMode = context.target.itemId === 'com.ibm.bi.dashboard.save';
			if (appViewType === 'EXPLORE'){
				return saveMode ? this.getContentView(context).canAuthor() : true;
			}
			return saveMode ? this.getContentView(context).canAuthor() && ErrorUtils.hasCapability(context.glassContext, 'canAuthorDashboard') : ErrorUtils.hasCapability(context.glassContext, 'canAuthorDashboard');
		},

		/**
		 * Hook to override require calls for the unit test.
		 */
		_require: function (modules, callback) {
			return require(modules, callback);
		},

		/**
		 * Prompt the user for a location and do a save as.
		 * @param {Object} context of selection (passed during onSelect)
		 * @param {string} id The Board Id
		 */
		saveAs: function (context, id) {
			var dashboardView = this.getDashboardView(context);
			var defaultName = dashboardView.boardModel.name;

			return this._getAncestors(context, id)
				.then(ancestors => {
					return context.glassContext.getSvc('.ContentDialogFactory').then(dialogFactory => {
						this._saveAsDialog = dialogFactory.createSaveAsDialog({
							glassContext: context.glassContext,
							defaultFileName: defaultName,
							service: {
								context: context,
								save: this.onSaveAs.bind(this)
							},
							ancestors: ancestors
						});
						this._saveAsDialog.open();
					});
				});
		},

		_getAncestors(context,id){
			return this._getContentService(context)
				.then((contentService) => contentService.getAncestors({ id }));
		},

		onSaveAs: function (service, selection, name, overwrite) {
			const context = service.context;
			return this._getSaveFeature(context)
				.saveAs(name, selection.url, overwrite, saveResponseFields)
				.then(this._onSaveAsSuccess.bind(this, context), this._onSaveAsError.bind(this, context))
				.then(this._invokeLifeCycleHandlers.bind(this, 'saveas.after', context))
				.finally(this._saveAsDialog.hide.bind(this._saveAsDialog));
		},

		_onSaveAsError: function (context, errorObject) {
			var errorCode = ErrorUtils.getErrorCode(errorObject.saveRequest);
			if (errorCode === 'cmDuplicateName') {
				var error = {
					'isDuplicate': true
				};
				return Promise.reject(error);
			} else if (errorCode.lastIndexOf('cmAddWithReplaceFailed', 0) === 0
					|| errorCode.lastIndexOf('cmInvalidReference', 0) === 0) {
				// error codes: cmAddWithReplaceFailed, cmAddWithReplaceFailedBadClass, cmInvalidReference2
				error = {
					'isReplaceFailed': true
				};
				return Promise.reject(error);

			} else {
				ErrorUtils.showError(context.glassContext, errorObject.saveRequest, errorObject.cmSpec);
				return Promise.resolve();
			}
		},

		_onSaveAsSuccess: function (context, params = {}) {

			const contentView = this.getContentView(context);
			contentView.setSavedInstance(params.savedSpec);


			// Call Content Service GET to trigger MRU
			return this._getContentService(context)
				.then((oContentService) => {
					return oContentService.onSaveAsSuccess({ url : params.url });
				}).done(function (dbInfo) {
					const dashboard = this._getDashboardApi(context);
					if(dbInfo){
						if(dbInfo.id){
							contentView.boardModel.set({ id: dbInfo.id }, { silent: true });
						}
						if(dbInfo.permissions){
							contentView.setPermissions(dbInfo.permissions);
						}
					}
					// Update the name in the dashboard model
					contentView.boardModel.set({
						name: params.name
					}, {
						silent: true
					});
					const segment = dashboard.getFeature('segment');
					const segmentType = { type : 'Updated Object' };
					segment.track('savedAs', () => segmentType );
					// Clear dirty flag in the UI
					dashboard.getFeature('DashboardState').setDirty(false);
					contentView.boardController.undoRedoController.registerSaveAction();
					// Update current cached app view to prevent Defect 235654
					context.glassContext.appController.updateCurrentCachedAppView();

					const appConfig = this._getAppConfig(context);
					if (appConfig && appConfig.saveThumbnail) {
						this._saveThumbnail(context, dbInfo && dbInfo.id);
					}

					var stringResources = this.getStringResourcesService(context);
					context.glassContext.appController.showToast(stringResources.get('dashboard_save_success'));

					return this._invokeSaveServiceHandlers(context, {
						saveAs: true,
						stringResources,
						context,
						spec: dashboard.getFeature('Serializer').toJSON()
					});
				}.bind(this));
		},

		/**
		 * Get glass service .DashboardContent
		 * @private
		 * @async
		 * @return {Promise<Object>} 
		 */
		_getContentService: function(context){
			return context.glassContext.getSvc('.DashboardContent');
		},

		save: function (context, bInvokeSaveServiceHandlers, bShowToast) {
			const dashboardView = this.getDashboardView(context);
			const dashboard = dashboardView.getDashboardApi();
			const id = dashboardView.getBoardId();
			const { boardModel } = dashboardView;

			let boardSpec = dashboard.getFeature('Serializer').toJSON();
			let _saveSpec = null;

			SaveActionUtil.cleanUnusedCustomColors(dashboard, boardSpec, boardModel);

			return this._getSaveFeature(context)
				.save(saveResponseFields)
				.then(({ savedSpec }) =>{
					if(savedSpec){
						_saveSpec = savedSpec;
					}
					return this._invokeLifeCycleHandlers('save.after', context);
				}).then(()=> {
					var stringResources = this.getStringResourcesService(context);
					const segment = dashboard.getFeature('segment');
					const segmentType = { type : 'Updated Object' };
					segment.track('saved', () => segmentType );
					// Clear dirty flag in the UI
					dashboard.getFeature('DashboardState').setDirty(false);
					dashboardView.boardController.undoRedoController.registerSaveAction();
					dashboardView.setSavedInstance(_saveSpec);
					const appConfig = this._getAppConfig(context);
					if (appConfig && appConfig.saveThumbnail) {
						this._saveThumbnail(context, id);
					}

					if (bShowToast) {
						context.glassContext.appController.showToast(stringResources.get('dashboard_save_success'));
					}

					if (bInvokeSaveServiceHandlers) {
						this._invokeSaveServiceHandlers(context, {
							saveAs: false,
							stringResources: stringResources,
							context: context,
							spec: boardSpec
						});
					}
				}).catch((request) => {
					var code = ErrorUtils.showError(context.glassContext, request);
					// The object is deleted.. try a save as
					if (code === 'cmEmptySelection') {
						this.saveAs(context);
					}
				});
		},

		_invokeLifeCycleHandlers: function( name, context ){
			const dashboardApi = this.getDashboardView(context).getDashboardApi();
			let lifeCycle = dashboardApi.getFeature('LifeCycle');
			return lifeCycle.invokeHandlers(name)
				.catch( error => {
					let logger  = dashboardApi.getGlassCoreSvc('.Logger');
					logger.error('[SaveActionHandler] Life cycle handlers error', error);
				});
		},

		/**
		 * Loop over the services that are in the saveServices collection to call their 'onDashboardSave' method
		 */
		_invokeSaveServiceHandlers: function(context, options) {
			const dashboard = this._getDashboardApi(context);
			return this._getSaveServiceExtensionCollection(context.glassContext, dashboard).then(function(collection) {
				const promises = [];
				if (collection) {
					// provide a call that will update the dashboard spec in CM if needed
					options.resaveDashboardSpecCallback = this.save.bind(this, context, false, false/*bShowToast*/);
					collection.forEach(function(extension) {
						const saveService = dashboard.getFeature(extension.name);
						if (saveService && saveService.onDashboardSave) {
							promises.push(saveService.onDashboardSave(options));
						}
					});
				}
				return Promise.all(promises);
			}.bind(this));
		},

		_getSaveServiceExtensionCollection: function (glassContext, dashboard) {
			const saveServices = dashboard.getCollectionConfig('saveServices');
			if (saveServices) {
				return Promise.all([glassContext.appController.findCollection(saveServices.id)]).then(function (extensions) {
					return extensions[0];
				});
			}
			return Promise.resolve();
		},

		_getAppConfig: function(context) {
			const content = this._getContextContent(context);
			return content && content.options && content.options.config;
		},

		_saveThumbnail: function(context, id) {
			return this._createThumbnail(context).then(thumbnailDataUri => {
				return this._getContentService(context).then(contentService => {
					return contentService.saveThumbnail( { thumbnailDataUri, id });
				});
			});
		},

		_createThumbnail: function(context) {
			const dashboardView = this.getDashboardView(context);
			const thumbnailSvc = dashboardView.getDashboardApi().getFeature('.Thumbnail');
			return thumbnailSvc && thumbnailSvc.generateImage(dashboardView.el);
		},

		_getSaveFeature: function(context){
			return this._getDashboardApi(context).getFeature('Save');
		}
	});

	return ActionHandler;
});
